import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, Tv, Landmark, Music, Heart, Users,
  Star, ArrowRight, CheckCircle, Globe, Eye, Handshake, Loader2, ExternalLink
} from 'lucide-react';
import { usePageVisibility } from '../hooks/usePageVisibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Partenaires = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageVisibility('page_partenaires_enabled');

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const res = await axios.get(`${API}/partners`);
        setPartners(res.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);

  const partnerTypes = [
    {
      icon: Landmark,
      title: 'Institutions',
      desc: 'Collectivités territoriales, offices de tourisme, conseils départementaux et régionaux',
      benefits: ['Soutien à la culture locale', 'Valorisation du territoire', 'Rayonnement régional'],
      color: 'gold'
    },
    {
      icon: Building2,
      title: 'Entreprises',
      desc: 'PME locales, grands groupes, sponsors privés souhaitant s\'associer à la culture bretonne',
      benefits: ['Visibilité premium', 'Image de marque positive', 'Réseau privilégié'],
      color: 'electric-cyan'
    },
    {
      icon: Tv,
      title: 'Médias',
      desc: 'Presse locale et nationale, radios, chaînes TV, médias en ligne',
      benefits: ['Contenu exclusif', 'Accès backstage', 'Partenariat éditorial'],
      color: 'green-400'
    },
    {
      icon: Music,
      title: 'Acteurs Culturels',
      desc: 'Festivals, salles de concert, écoles de musique, associations culturelles',
      benefits: ['Synergie événementielle', 'Cross-promotion', 'Réseau artistique'],
      color: 'purple-400'
    },
  ];

  const whyPartner = [
    { icon: Eye, title: 'Visibilité', desc: 'Exposition auprès de milliers de passionnés de musique bretonne' },
    { icon: Heart, title: 'Engagement', desc: 'Association à un projet culturel porteur de sens et de valeurs' },
    { icon: Globe, title: 'Rayonnement', desc: 'Participation au rayonnement de la culture bretonne' },
    { icon: Users, title: 'Communauté', desc: 'Accès à une communauté engagée et fidèle' },
  ];

  // Group partners by type
  const premiumPartners = partners.filter(p => p.level === 'premium');
  const goldPartners = partners.filter(p => p.level === 'gold');
  const standardPartners = partners.filter(p => p.level === 'standard' || !p.level);

  return (
    <main className="min-h-screen bg-obsidian pt-32 pb-20" data-testid="partenaires-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-20">
        <div className="max-w-3xl">
          <span className="text-gold text-sm uppercase tracking-widest">Partenariats</span>
          <h1 className="font-syne text-4xl md:text-6xl font-extrabold tracking-tighter text-white mt-4 mb-6">
            Devenez<br />
            <span className="text-gold-gradient">Partenaire</span>
          </h1>
          <p className="text-lg text-white/60">
            Associez votre image à un événement culturel majeur. Les Stered an Noz Awards offrent une plateforme unique pour soutenir et valoriser la musique bretonne tout en bénéficiant d'une visibilité exceptionnelle.
          </p>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-24 bg-obsidian-light">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-gold text-sm uppercase tracking-widest">Avantages</span>
            <h2 className="font-syne text-3xl md:text-5xl font-bold tracking-tight text-white mt-4 mb-4">
              Pourquoi Nous Rejoindre ?
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyPartner.map((item, idx) => (
              <div key={idx} className="p-8 border border-white/10 hover:border-gold/50 transition-all text-center group">
                <div className="w-16 h-16 mx-auto mb-6 border border-white/10 group-hover:border-gold/50 flex items-center justify-center transition-colors">
                  <item.icon className="w-8 h-8 text-gold" />
                </div>
                <h3 className="font-syne text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-gold text-sm uppercase tracking-widest">Catégories</span>
            <h2 className="font-syne text-3xl md:text-5xl font-bold tracking-tight text-white mt-4 mb-4">
              Types de Partenariats
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Différentes formules adaptées à vos objectifs et votre structure
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {partnerTypes.map((type, idx) => (
              <div key={idx} className="p-8 border border-white/10 bg-obsidian-light hover:border-gold/30 transition-all">
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-14 h-14 flex items-center justify-center border border-${type.color}/30 bg-${type.color}/5`}>
                    <type.icon className={`w-7 h-7 text-${type.color}`} />
                  </div>
                  <div>
                    <h3 className="font-syne text-2xl font-bold text-white">{type.title}</h3>
                    <p className="text-white/50 text-sm mt-1">{type.desc}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {type.benefits.map((benefit, bidx) => (
                    <div key={bidx} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                      <span className="text-white/70">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Logos */}
      <section className="py-24 bg-obsidian-light">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-gold text-sm uppercase tracking-widest">Ils nous font confiance</span>
            <h2 className="font-syne text-3xl md:text-4xl font-bold tracking-tight text-white mt-4">
              Nos Partenaires
            </h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-gold animate-spin" />
            </div>
          ) : partners.length > 0 ? (
            <div className="space-y-12">
              {/* Premium Partners */}
              {premiumPartners.length > 0 && (
                <div>
                  <h3 className="text-center text-sm uppercase tracking-widest text-gold mb-6">Partenaires Premium</h3>
                  <div className="flex flex-wrap justify-center gap-8">
                    {premiumPartners.map(partner => (
                      <PartnerCard key={partner.id} partner={partner} size="large" />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Gold Partners */}
              {goldPartners.length > 0 && (
                <div>
                  <h3 className="text-center text-sm uppercase tracking-widest text-white/50 mb-6">Partenaires Gold</h3>
                  <div className="flex flex-wrap justify-center gap-6">
                    {goldPartners.map(partner => (
                      <PartnerCard key={partner.id} partner={partner} size="medium" />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Standard Partners */}
              {standardPartners.length > 0 && (
                <div>
                  <h3 className="text-center text-sm uppercase tracking-widest text-white/30 mb-6">Partenaires</h3>
                  <div className="flex flex-wrap justify-center gap-4">
                    {standardPartners.map(partner => (
                      <PartnerCard key={partner.id} partner={partner} size="small" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Placeholder for partner logos */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                {[...Array(6)].map((_, idx) => (
                  <div key={idx} className="aspect-[3/2] border border-white/10 bg-obsidian flex items-center justify-center group hover:border-gold/30 transition-colors">
                    <div className="text-center">
                      <Handshake className="w-8 h-8 text-white/20 mx-auto mb-2 group-hover:text-gold/50 transition-colors" />
                      <span className="text-xs text-white/30">Partenaire</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-center text-white/40 mt-8 text-sm">
                Édition inaugurale 2026 — Partenaires à venir
              </p>
            </>
          )}
        </div>
      </section>

      {/* Benefits Detail */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-gold text-sm uppercase tracking-widest">Ce que vous obtenez</span>
              <h2 className="font-syne text-3xl md:text-4xl font-bold tracking-tight text-white mt-4 mb-6">
                Bénéfices Partenaires
              </h2>
              
              <div className="space-y-6">
                {[
                  { title: 'Visibilité digitale', desc: 'Logo sur le site, mentions sur les réseaux sociaux, newsletter dédiée' },
                  { title: 'Présence événementielle', desc: 'Stand au gala, logo sur les supports print, mentions lors de la cérémonie' },
                  { title: 'Expériences exclusives', desc: 'Invitations VIP au gala, accès backstage, meet & greet artistes' },
                  { title: 'Contenu co-brandé', desc: 'Création de contenus communs, interviews, reportages' },
                  { title: 'Réseau privilégié', desc: 'Accès au réseau d\'acteurs culturels bretons, événements privés' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-gold/10 border border-gold/30 flex-shrink-0">
                      <Star className="w-4 h-4 text-gold" />
                    </div>
                    <div>
                      <h4 className="font-syne font-bold text-white mb-1">{item.title}</h4>
                      <p className="text-sm text-white/50">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square bg-obsidian-light border border-white/10 p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-gold/10 to-electric-blue/10" />
                <div className="relative h-full flex flex-col justify-center items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mb-6">
                    <Handshake className="w-10 h-10 text-gold" />
                  </div>
                  <p className="font-syne text-2xl font-bold text-white mb-2">Partenariat sur-mesure</p>
                  <p className="text-white/50">Nous adaptons notre offre à vos besoins</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-obsidian-light">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="p-12 border border-gold/30 bg-gradient-to-br from-gold/5 to-transparent text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-gold/10 blur-3xl" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-electric-blue/10 blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="font-syne text-3xl md:text-5xl font-bold text-white mb-4">
                Intéressé ?
              </h2>
              <p className="text-xl text-white/60 mb-8 max-w-xl mx-auto">
                Contactez-nous pour discuter de votre partenariat et recevoir notre dossier complet.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:partenaires@steredannoz.bzh"
                  className="bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-10 py-5 text-sm transition-all hover:scale-105 inline-flex items-center justify-center gap-3"
                  data-testid="contact-partner-btn"
                >
                  Nous contacter
                  <ArrowRight className="w-5 h-5" />
                </a>
                <button
                  className="border border-white/20 hover:border-white text-white px-10 py-5 uppercase tracking-wider text-sm hover:bg-white/5 transition-all"
                >
                  Télécharger le dossier
                </button>
              </div>
              
              <p className="mt-8 text-sm text-white/40">
                partenaires@steredannoz.bzh
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Partner Card Component
const PartnerCard = ({ partner, size = 'medium' }) => {
  const sizeClasses = {
    large: 'w-48 h-32 p-4',
    medium: 'w-36 h-24 p-3',
    small: 'w-28 h-20 p-2'
  };
  
  const content = (
    <div className={`${sizeClasses[size]} border border-white/10 bg-obsidian flex items-center justify-center hover:border-gold/50 transition-all group`}>
      {partner.logo_url ? (
        <img 
          src={partner.logo_url} 
          alt={partner.name} 
          className="max-w-full max-h-full object-contain opacity-70 group-hover:opacity-100 transition-opacity" 
        />
      ) : (
        <div className="text-center">
          <Handshake className="w-6 h-6 text-white/30 mx-auto mb-1 group-hover:text-gold transition-colors" />
          <span className="text-xs text-white/50 group-hover:text-white transition-colors">{partner.name}</span>
        </div>
      )}
    </div>
  );
  
  if (partner.website_url) {
    return (
      <a href={partner.website_url} target="_blank" rel="noopener noreferrer" title={partner.name}>
        {content}
      </a>
    );
  }
  
  return content;
};

export default Partenaires;
